import {Helmet} from '@dr.pogodin/react-helmet';
import {DataStatus} from 'platform/components';
import {
  Box,
  getSize,
  getValueByDevice,
  Hide,
  HStack,
  Show,
  Space,
  useDevice,
  VStack,
} from 'platform/foundation';

import {isNotNil} from 'ramda-adjunct';

import {suffixTestId, TestIdProps} from 'shared/web';

import {PageBackgroundGradient} from '../../components/PageBackgroundGradient/PageBackgroundGradient';
import {StickyContainer} from '../../components/StickyContainer/StickyContainer';
import {VehicleHeader} from '../../components/VehicleHeader/VehicleHeader';
import {useCatalogue} from '../../hooks/useCatalogue';
import {usePriceReport} from '../../hooks/usePriceReport';
import i18n from '../../i18n/i18n';
import {AttractivenessCard} from './(sections)/AttractivenessCard/AttractivenessCard';
import {MaxPurchasePriceCard} from './(sections)/MaxPurchasePriceCard/MaxPurchasePriceCard';
import {RealSellingPriceCard} from './(sections)/RealSellingPriceCard/RealSellingPriceCard';
import {SavedFiltersCard} from './(sections)/SavedFiltersCard/SavedFiltersCard';
import {StatisticsCardAge} from './(sections)/StatisticsCardAge/StatisticsCardAge';
import {StatisticsCardColor} from './(sections)/StatisticsCardColor/StatisticsCardColor';
import {StatisticsCardFeatureLevel} from './(sections)/StatisticsCardFeaturesLevel/StatisticsCardFeatureLevel';
import {StatisticsCardMileage} from './(sections)/StatisticsCardMileage/StatisticsCardMileage';
import {StatisticsCardPrice} from './(sections)/StatisticsCardPrice/StatisticsCardPrice';
import {StatisticsCardVAT} from './(sections)/StatisticsCardVAT/StatisticsCardVAT';
import {StatisticsCardVehicle} from './(sections)/StatisticsCardVehicle/StatisticsCardVehicle';
import {UserComplaintCard} from './(sections)/UserComplaintCard/UserComplaintCard';
import {VehicleParametersCard} from './(sections)/VehicleParametersCard/VehicleParametersCard';
import {useIsPrintForced} from './hooks/useIsPrintForced';

const PRINT_CONTENT_WIDTH = 240;

export function OverviewPage(props: TestIdProps) {
  const {priceReport, requestData, isLoading, isError} = usePriceReport();
  const catalogue = useCatalogue();
  const device = useDevice();
  const {isPrint, getResponsiveValue} = useIsPrintForced();
  const currentMarket = priceReport?.markets?.find((m) => m.name === priceReport?.market);
  const maxContentWidth = getResponsiveValue(
    '100%',
    `calc(100% - ${getSize(86)})` as const,
    `calc(100% - ${getSize(114)})` as const,
    `calc(100% - ${getSize(114)})` as const,
    PRINT_CONTENT_WIDTH
  );

  return (
    <>
      <Helmet title={i18n.t('page.overview.title')} />
      <DataStatus isLoading={isLoading} isError={isError} minHeight={20}>
        <PageBackgroundGradient>
          {isNotNil(priceReport) && (
            <VStack shrink={1} maxWidth={315} width="100%">
              <VehicleHeader
                priceReport={priceReport}
                catalogue={catalogue}
                data-testid={suffixTestId('vehicleHeader', props)}
              />
              <Space vertical={6} />
              <HStack
                spacing={6}
                justify="center"
                wrap={getResponsiveValue(true, false, false, false, false)}
              >
                <Hide onMobile when={isPrint}>
                  <Box width={['100%', 80, 108]} flexShrink={0}>
                    <StickyContainer
                      offset={4}
                      isDisabled={getValueByDevice(device, true, false)}
                      data-testid={suffixTestId('stickyContainer', props)}
                    >
                      <VStack spacing={6}>
                        <VehicleParametersCard
                          priceReport={priceReport}
                          request={requestData}
                          catalogue={catalogue}
                          data-testid={suffixTestId('vehicleParametersCard', props)}
                        />
                        <UserComplaintCard
                          created={priceReport.created}
                          data-testid={suffixTestId('userComplaintCard', props)}
                        />
                      </VStack>
                    </StickyContainer>
                  </Box>
                </Hide>
                <VStack spacing={6} grow={1} maxWidth={maxContentWidth}>
                  <RealSellingPriceCard
                    priceReport={priceReport}
                    market={currentMarket}
                    data-testid={suffixTestId('realSellingPriceCard', props)}
                  />
                  <Show when={device === 'mobile' || isPrint}>
                    <VehicleParametersCard
                      priceReport={priceReport}
                      request={requestData}
                      catalogue={catalogue}
                      data-testid={suffixTestId('vehicleParametersCardMobile', props)}
                    />
                  </Show>
                  <Show when={isPrint}>
                    <UserComplaintCard
                      created={priceReport.created}
                      data-testid={suffixTestId('userComplaintCardPrint', props)}
                    />
                  </Show>
                  <SavedFiltersCard
                    priceReport={priceReport}
                    data-testid={suffixTestId('savedFiltersCard', props)}
                  />
                  <MaxPurchasePriceCard
                    priceReport={priceReport}
                    data-testid={suffixTestId('maxPurchasePriceCard', props)}
                  />
                  <AttractivenessCard
                    priceReport={priceReport}
                    data-testid={suffixTestId('attractivenessPriceCard', props)}
                  />
                  <StatisticsCardPrice
                    market={currentMarket}
                    priceReport={priceReport}
                    data-testid={suffixTestId('statisticsCardPrice', props)}
                  />
                  <StatisticsCardVehicle
                    market={currentMarket}
                    turnover={priceReport.turnover}
                    sellingPrice={priceReport.sellingPrice}
                    data-testid={suffixTestId('statisticsCardVehicle', props)}
                  />
                  <StatisticsCardMileage
                    market={currentMarket}
                    vehicle={priceReport.vehicle}
                    country={priceReport.market}
                    data-testid={suffixTestId('statisticsCardMileage', props)}
                  />
                  <StatisticsCardFeatureLevel
                    market={currentMarket}
                    priceReport={priceReport}
                    data-testid={suffixTestId('statisticsCardFeatureLevel', props)}
                  />
                  <StatisticsCardAge
                    market={currentMarket}
                    priceReport={priceReport}
                    data-testid={suffixTestId('statisticsCardAge', props)}
                  />
                  <StatisticsCardVAT
                    market={currentMarket}
                    vehicle={priceReport.vehicle}
                    data-testid={suffixTestId('statisticsCardVAT', props)}
                  />
                  <StatisticsCardColor
                    market={currentMarket}
                    vehicle={priceReport.vehicle}
                    data-testid={suffixTestId('statisticsCardColor', props)}
                  />
                  <Show onMobile>
                    <UserComplaintCard
                      created={priceReport.created}
                      data-testid={suffixTestId('userComplaintCard', props)}
                    />
                  </Show>
                </VStack>
              </HStack>
            </VStack>
          )}
        </PageBackgroundGradient>
      </DataStatus>
    </>
  );
}
